import React from "react";
import { variantURL, fallbackToOriginal } from "../../utils/imageVariant";

/**
 * UserSelectionConfirmModal component for confirming user selection actions
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Array} props.selectedUsers - Array of selected users
 * @param {Function} props.onConfirm - Handler when confirm is clicked
 * @param {Function} props.onCancel - Handler when cancel is clicked
 * @param {Function} props.onRemoveUser - Handler when a user is removed from selection (optional)
 * @param {string} props.title - Modal title (default: "Confirm Invite")
 * @param {string} props.confirmText - Confirm button text (default: "Confirm")
 * @param {string} props.description - Description text (optional)
 * @param {boolean} props.isLoading - Whether the action is in progress
 * @param {React.ReactNode} props.children - Extra content between the grid and the buttons (optional)
 */
export function UserSelectionConfirmModal({
  isOpen,
  selectedUsers,
  onConfirm,
  onCancel,
  onRemoveUser,
  title = "Confirm Invite",
  confirmText = "Confirm",
  description,
  isLoading = false,
  children,
}) {
  if (!isOpen) return null;

  const defaultDescription = `You are about to send invites to ${selectedUsers.length} user${selectedUsers.length > 1 ? "s" : ""
    }:`;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center"
      onClick={onCancel}
    >
      <div
        className="relative mx-4 p-6 border border-white/30 max-w-2xl w-full overflow-y-auto shadow-lg rounded-lg bg-white dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
        <p className="text-gray-600 dark:text-gray-300 mb-4">{description || defaultDescription}</p>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 max-h-64 overflow-y-auto mb-6">
          {selectedUsers.map((user) => (
            <div key={user.id} className="text-center relative group">
              {/* Remove button */}
              {onRemoveUser && !isLoading && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveUser(user);
                  }}
                  className="absolute top-0 right-5 z-10 w-5 h-5 rounded-full bg-gray-500 text-white flex items-center justify-center transition-colors focus:outline-none"
                  title={`Remove ${user.name || "user"}`}
                >
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
              <div className="w-16 h-16 mx-auto rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                {user.photoURL ? (
                  <img
                    src={variantURL(user.photoURL, 200)}
                    onError={fallbackToOriginal(user.photoURL)}
                    alt={user.name || "User"}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary to-primaryDark flex items-center justify-center">
                    <span className="text-white text-xl font-bold">
                      {(user.name || "?").charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-700 dark:text-gray-200 mt-1 truncate">
                {user.name || "Unnamed"}
              </p>
            </div>
          ))}
        </div>
        {children && <div className="mb-6">{children}</div>}
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className={`px-4 py-2 font-medium rounded-md transition-colors ${isLoading
              ? "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
              : "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
              }`}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading || selectedUsers.length === 0}
            className={`px-4 py-2 font-medium rounded-md transition-colors flex items-center gap-2 ${isLoading || selectedUsers.length === 0
              ? "bg-gray-400 text-white cursor-not-allowed"
              : "bg-primary text-white hover:bg-primaryDark"
              }`}
          >
            {isLoading && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
